import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, Star, Share2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openShare } from "@/lib/share";
import { CandyBoard } from "@/components/candy/CandyBoard";
import { LevelMap, LevelIntro, Hud, Result, Lives, Board, useIcons, Boosters, MoreMoves, DailyCard } from "@/components/candy/CandyParts";
import { MusicToggle } from "@/components/games/GameMusic";
import { InviteLock } from "@/components/growth/InviteLock";
import { UserAvatar } from "@/components/UserBits";

const DEMO = { n: 0, moves: 999, colors: 6, kind: "score", stars: [1e9, 1e9, 1e9], seed: 7 };
const fallback = (code) => `${window.location.origin}/zuckerherz?ref=${code || ""}`;
const share = (code, text) => openShare({ kind: "game", target: "candy", text, title: "Freunde herausfordern", msg: text, fallbackUrl: fallback(code) });

const Title = ({ size = "text-5xl" }) => (
  <h1 className={`hero-plain cz-serif ${size} font-extrabold italic leading-[0.95] text-white`} data-testid="candy-title">Zuckerherz<br /><span className="cz-gold-text">Saga</span></h1>
);

function Guest() {
  const [sp] = useSearchParams();
  const ref = sp.get("ref") || "";
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/candy/public?ref=${encodeURIComponent(ref)}`).then(({ data }) => setD(data)).catch(() => setD({})); }, [ref]);
  useEffect(() => { sessionStorage.setItem("after_auth", "/zuckerherz"); window.scrollTo(0, 0); }, []);
  return (
    <div className="min-h-[100dvh] cz-bg text-white" data-testid="candy-guest">
      <div className="max-w-md mx-auto px-5 pt-10 pb-20 space-y-6 cz-stagger">
        <p className="text-[10px] font-extrabold tracking-[0.35em] text-amber-100/70">ITZPLAY · PUZZLE</p>
        <Title />
        {d?.inviter ? (
          <div className="cz-glass flex items-center gap-3 rounded-3xl p-3" data-testid="candy-challenge">
            <UserAvatar name={d.inviter.name} picture={d.inviter.picture} size="w-12 h-12" noStory />
            <p className="text-sm"><b>{d.inviter.name}</b> lädt dich ein{d.inviter.level ? <> – schon bei Level <b className="text-amber-200">{d.inviter.level}</b> mit {d.inviter.stars} ★</> : ""}. Kannst du mithalten?</p>
          </div>
        ) : <p className="text-white/75">Tausche funkelnde Herzen, Juwelen und Perlen, löse glänzende Kettenreaktionen aus und sammle Sterne in 60 Levels – für echte Punkte auf deinem Konto.</p>}
        <div className="cz-frame rounded-[30px] p-2.5"><CandyBoard level={DEMO} demo testId="candy-demo" /></div>
        <div className="space-y-2.5">
          <Link to={`/registrieren${ref ? `?ref=${encodeURIComponent(ref)}` : ""}`} data-testid="candy-guest-register" className="cz-btn w-full h-14 rounded-full font-extrabold text-lg flex items-center justify-center">Kostenlos mitspielen</Link>
          <Link to="/login" data-testid="candy-guest-login" className="w-full h-12 rounded-full cz-glass font-bold flex items-center justify-center">Ich habe schon ein Konto</Link>
        </div>
        {d?.top?.length > 0 && <Board title="Die besten Spieler" rows={d.top} testId="candy-guest-top" />}
      </div>
    </div>
  );
}

function Play({ lv, session, icons, onFinished, onQuit, boosts, points, setPoints }) {
  const [hud, setHud] = useState({ moves: lv.moves, score: 0, collected: {}, iceLeft: lv.ice?.length || 0 });
  const [combo, setCombo] = useState(null);
  const [used, setUsed] = useState(0);
  const [hammer, setHammer] = useState(false);
  const [ask, setAsk] = useState(null);
  const [buying, setBuying] = useState(false);
  const tm = useRef(null);
  const ctrl = useRef(null);
  const buy = async (kind) => {
    try {
      const { data } = await api.post("/candy/boost", { session_id: session, kind });
      setUsed(data.used); setPoints(data.points); return true;
    } catch (e) { toast.error(formatErr(e)); return false; }
  };
  const toggleHammer = () => { const on = !hammer; if (on && ctrl.current?.busy()) return; setHammer(on); ctrl.current?.hammer(on); };
  const shuffle = async () => { if (ctrl.current?.busy()) { toast("Einen Moment – die Steine fallen noch ✨"); return; } if (await buy("shuffle")) ctrl.current?.shuffle(); };
  const onOutOfMoves = useCallback(() => new Promise((resolve) => setAsk({ resolve })), []);
  const answer = async (yes) => {
    if (yes) { setBuying(true); const ok = await buy("moves"); setBuying(false); if (!ok) return; }
    ask.resolve(yes); setAsk(null);
  };
  const onCombo = useCallback((t) => { if (!t) return; setCombo({ t, k: Date.now() }); clearTimeout(tm.current); tm.current = setTimeout(() => setCombo(null), 1100); }, []);
  const quit = () => { if (window.confirm("Level verlassen? Das eingesetzte Leben ist dann verloren.")) onQuit(hud); };
  return (
    <div className="fixed inset-0 z-[80] cz-bg flex flex-col select-none" data-testid="candy-play" data-session={session}>
      <Hud lv={lv} icons={icons} hud={hud} onQuit={quit} />
      <div className="flex-1 flex items-center justify-center px-2.5 py-3 relative">
        <div className="cz-frame rounded-[30px] p-2 w-full max-w-[560px]">
          <CandyBoard level={lv} ctrl={ctrl} onHud={setHud} onCombo={onCombo} onDone={onFinished} onBoost={buy} onOutOfMoves={onOutOfMoves} onHammerDone={() => setHammer(false)} />
        </div>
        {hammer && <p className="absolute top-2 left-0 right-0 text-center text-sm font-bold text-amber-100 pointer-events-none" data-testid="candy-hammer-hint">Tippe auf einen Stein, um ihn zu zerschlagen</p>}
        {combo && <p key={combo.k} className="absolute left-0 right-0 top-1/2 -translate-y-1/2 text-center pointer-events-none cz-combo cz-serif italic font-extrabold" data-testid="candy-combo">{combo.t}</p>}
      </div>
      <Boosters cfg={boosts} used={used} points={points} hammerOn={hammer} onHammer={toggleHammer} onShuffle={shuffle} />
      <div className="absolute top-[calc(env(safe-area-inset-top)+10px)] right-3"><MusicToggle /></div>
      {ask && <MoreMoves price={boosts.moves} points={points} canBuy={used < boosts.per_try} busy={buying} onBuy={() => answer(true)} onGiveUp={() => answer(false)} />}
    </div>
  );
}

function Member({ user }) {
  const icons = useIcons();
  const [d, setD] = useState(null);
  const [pick, setPick] = useState(null);
  const [play, setPlay] = useState(null);
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("week");
  const load = useCallback(() => api.get("/candy").then(({ data }) => setD(data)).catch((e) => { toast.error(formatErr(e)); setD({ error: true }); }), []);
  useEffect(() => { load(); window.scrollTo(0, 0); }, [load]);
  if (!d) return <div className="min-h-[100dvh] cz-bg" data-testid="candy-loading" />;
  if (d.error) return (
    <div className="min-h-[100dvh] cz-bg text-white px-6 pt-24 text-center space-y-4" data-testid="candy-error">
      <Title size="text-4xl" />
      <p className="text-white/75">Das Spiel konnte gerade nicht geladen werden – vielleicht ist es pausiert.</p>
      <button onClick={() => { setD(null); load(); }} data-testid="candy-error-retry" className="cz-btn h-12 px-8 rounded-full font-extrabold">Erneut versuchen</button>
      <Link to="/spiele" className="block text-sm text-white/60 underline" data-testid="candy-error-back">Zurück zu den Spielen</Link>
    </div>
  );

  const start = async (lv) => {
    setBusy(true);
    try {
      const { data } = await api.post("/candy/start", { level: lv.n });
      setD((x) => ({ ...x, lives: data.lives })); setPick(null); setRes(null); setPlay({ lv, session: data.session_id });
    } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };
  const finish = async (r) => {
    try {
      const { data } = await api.post("/candy/finish", { session_id: play.session, won: r.won, score: r.score, moves_left: r.movesLeft });
      setRes(data);
      setD((x) => ({ ...x, points: data.user_points ?? x.points, lives: data.lives, next_life_in: data.next_life_in, unlocked: data.unlocked, total: data.total, today_points: data.today_points,
        daily_best: data.daily ? data.daily_best : x.daily_best,
        progress: data.daily ? x.progress : data.stars ? { ...x.progress, [String(data.level)]: Math.max(data.stars, x.progress[String(data.level)] || 0) } : x.progress,
        best: data.daily ? x.best : { ...x.best, [String(data.level)]: Math.max(data.score, x.best[String(data.level)] || 0) } }));
    } catch (e) { toast.error(formatErr(e)); setPlay(null); }
  };
  const toMap = () => { setPlay(null); setRes(null); load(); };
  const lvAt = (n) => d.levels[n - 1];
  const rows = tab === "week" ? d.week_top : d.top;
  return (
    <div className="min-h-[100dvh] cz-bg text-white" data-testid="candy-lobby">
      <div className="sticky top-0 z-20 cz-topbar">
        <div className="max-w-md mx-auto px-4 pt-[calc(env(safe-area-inset-top)+10px)] pb-3 flex items-center gap-2">
          <Link to="/spiele" aria-label="Zurück" data-testid="candy-back" className="w-10 h-10 rounded-full cz-glass flex items-center justify-center"><ArrowLeft className="w-5 h-5" /></Link>
          <Lives lives={d.lives} max={d.max_lives} nextIn={d.next_life_in} onRefill={load} />
          <span className="cz-glass rounded-full h-10 px-3.5 inline-flex items-center gap-1.5 font-extrabold" data-testid="candy-total-stars"><Star className="w-4 h-4 text-amber-300" fill="currentColor" strokeWidth={0} />{d.total}</span>
          <span className="ml-auto"><MusicToggle /></span>
        </div>
      </div>
      <div className="max-w-md mx-auto px-5 pt-4 space-y-4 cz-stagger">
        <Title size="text-4xl" />
        <InviteLock gate="games" dark />
        <div className="cz-glass rounded-3xl p-4" data-testid="candy-daily">
          <div className="flex items-center justify-between text-sm"><span className="text-white/75">Heute verdient</span><span className="font-extrabold tabular-nums"><span className="text-amber-200">{d.today_points}</span> / {d.daily_cap} Punkte</span></div>
          <div className="mt-2 h-2 rounded-full bg-black/30 overflow-hidden"><div className="h-full rounded-full bg-gradient-to-r from-rose-400 to-amber-200" style={{ width: `${Math.min(100, (d.today_points / d.daily_cap) * 100)}%` }} /></div>
          <p className="mt-2 text-[11px] text-white/50">+{d.points_per_star} Punkte je neuem Stern · +{d.win_point} pro gewonnenem Level</p>
        </div>
      </div>
      <div className="max-w-md mx-auto px-5 pt-4"><DailyCard lv={d.daily} icons={icons} best={d.daily_best} top={d.daily_top} prizes={d.daily_prizes} onPlay={() => setPick(d.daily)} friends={d.daily_friends} onShare={() => share(d.code, `Spiel heute mit mir das Tages-Level der Zuckerherz Saga – wer holt mehr Punkte? 💖`)} /></div>
      <LevelMap levels={d.levels} progress={d.progress} unlocked={d.unlocked} me={user} onPick={setPick} friends={d.friends} />
      <div className="max-w-md mx-auto px-5 pb-32 space-y-4">
        <button onClick={() => share(d.code, `Ich bin in der Zuckerherz Saga schon bei Level ${d.unlocked} mit ${d.total} ★ – schaffst du mehr? 💖`)} data-testid="candy-share" className="cz-btn w-full h-13 py-3.5 rounded-full font-extrabold inline-flex items-center justify-center gap-2"><Share2 className="w-5 h-5" /> Freunde herausfordern</button>
        <div className="flex gap-2">
          {[["week", "Diese Woche"], ["all", "Allzeit"]].map(([k, l]) => <button key={k} onClick={() => setTab(k)} data-testid={`candy-tab-${k}`} className={`h-9 px-4 rounded-full text-sm font-bold ${tab === k ? "bg-amber-200 text-[#3b0a1f]" : "cz-glass text-white/75"}`}>{l}</button>)}
        </div>
        <Board title={tab === "week" ? "Sterne dieser Woche" : "Bestenliste"} rows={rows} testId="candy-board-top" />
        <p className="text-center text-[11px] text-white/40 inline-flex items-center justify-center gap-1 w-full"><Sparkles className="w-3 h-3" /> 4 Treffer = Streifen · L/T-Form = Goldpaket · 5 in Reihe = Schokotrüffel</p>
      </div>
      {pick && <LevelIntro lv={pick} icons={icons} best={pick.daily ? d.daily_best.score : d.best[String(pick.n)] || 0} stars={pick.daily ? d.daily_best.stars : d.progress[String(pick.n)] || 0} lives={d.lives} busy={busy} onPlay={() => start(pick)} onClose={() => setPick(null)} />}
      {play && <Play key={play.session} lv={play.lv} session={play.session} icons={icons} onFinished={finish} boosts={d.boosts} points={d.points} setPoints={(p) => setD((x) => ({ ...x, points: p }))} onQuit={(h) => { api.post("/candy/finish", { session_id: play.session, won: false, score: h.score, moves_left: h.moves }).catch(() => {}); toMap(); }} />}
      {play && res && (
        <Result res={res} lv={play.lv} lives={d.lives}
          onNext={() => { const nx = lvAt(play.lv.n + 1); setPlay(null); setRes(null); setPick(nx); }}
          onRetry={() => start(play.lv)} onMap={toMap}
          onShare={() => share(d.code, res.daily ? `Ich habe heute ${res.score.toLocaleString("de-DE")} Punkte im Tages-Level der Zuckerherz Saga geholt – schlägst du mich? 💖` : res.won ? `Level ${res.level} in der Zuckerherz Saga mit ${res.stars} ★ und ${res.score.toLocaleString("de-DE")} Punkten geschafft – schaffst du das auch? 💖` : `Level ${res.level} in der Zuckerherz Saga ist knifflig – probier du es mal! 💖`)} />
      )}
    </div>
  );
}

export default function CandySaga() {
  const { user } = useAuth();
  return user ? <Member user={user} /> : <Guest />;
}
